// Quick navigation ("awesomebar"): Ctrl/Cmd + K opens a list of every page the signed-in user may open.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Dialog, InputAdornment, List, ListItemButton, ListItemText, OutlinedInput, Typography } from '@mui/material';
import { Search as SearchIcon } from 'lucide-react';

import { selectUserMenu } from '../../../../Redux/slices/authSlice';

const flatten = (items = [], trail = []) =>
  items.flatMap((i) => {
    const here = [...trail, i.title];
    const self = i.url ? [{ title: i.title, path: here.slice(0, -1).filter((t) => t !== 'Main Menu').join(' › '), url: i.url }] : [];
    return [...self, ...flatten(i.children || [], here)];
  });

export default function Search() {
  const menu = useSelector(selectUserMenu);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  const pages = useMemo(() => flatten(menu), [menu]);
  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return pages.slice(0, 12);
    return pages.filter((p) => `${p.title} ${p.path}`.toLowerCase().includes(needle)).slice(0, 12);
  }, [pages, q]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => { setActive(0); }, [q]);

  const go = (item) => {
    if (!item) return;
    setOpen(false);
    setQ('');
    navigate(item.url);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === 'Enter') { e.preventDefault(); go(results[active]); }
  };

  return (
    <>
      <Box sx={{ width: '100%', ml: { xs: 0, md: 1 } }}>
        <OutlinedInput
          size="small"
          readOnly
          onClick={() => setOpen(true)}
          startAdornment={<InputAdornment position="start"><SearchIcon size={16} strokeWidth={1.7} color="#8c8c8c" /></InputAdornment>}
          endAdornment={
            <InputAdornment position="end">
              <Typography variant="caption" sx={{ px: 0.75, py: 0.1, border: '1px solid', borderColor: 'grey.300', borderRadius: 1, color: 'text.secondary', fontSize: 11 }}>
                Ctrl K
              </Typography>
            </InputAdornment>
          }
          placeholder="Search pages"
          sx={{ width: { xs: '100%', md: 280 }, cursor: 'pointer', bgcolor: 'grey.50', '& input': { cursor: 'pointer' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'grey.200' } }}
        />
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" slotProps={{ paper: { sx: { position: 'absolute', top: 72, m: 0 } } }}>
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <OutlinedInput
            fullWidth
            autoFocus
            inputRef={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Type a page name, then press Enter"
            startAdornment={<InputAdornment position="start"><SearchIcon size={16} strokeWidth={1.7} color="#8c8c8c" /></InputAdornment>}
          />
        </Box>
        <List dense sx={{ maxHeight: 360, overflow: 'auto', py: 0.5 }}>
          {results.length === 0 && (
            <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>No matching page.</Typography>
          )}
          {results.map((r, i) => (
            <ListItemButton key={r.url + r.title} selected={i === active} onClick={() => go(r)} onMouseEnter={() => setActive(i)} sx={{ borderRadius: 1.5, mx: 1 }}>
              <ListItemText primary={r.title} secondary={r.path || null} primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} secondaryTypographyProps={{ fontSize: 12 }} />
            </ListItemButton>
          ))}
        </List>
      </Dialog>
    </>
  );
}
